import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FaPlus,
  FaEdit,
  FaTrash,
  FaPlay,
  FaChartBar,
  FaSpinner,
  FaSearch
} from 'react-icons/fa';
import DataApi from '../../api/DataApi';
import { formatDateTime } from '../../utils/formatUtils';
import {
  formatRunAsLabel,
  getLoggedInUserId,
  loggedInUserRunAsLabel,
  pickRecordId,
  runningUserApiRowToOption,
} from '../../utils/dashboardRunUserLabel';
import './css/DashboardManager.css';

const DashboardManager = () => {
  const navigate = useNavigate();
  const [dashboards, setDashboards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingDashboard, setEditingDashboard] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredDashboards, setFilteredDashboards] = useState([]);

  // Form states
  const emptyForm = () => ({
    name: '',
    label: '',
    description: '',
    view_type: 'STATIC_USER',
    running_user_id: '',
  });

  const [formData, setFormData] = useState(emptyForm());
  const [runningUserOptions, setRunningUserOptions] = useState([]);
  const [runningUsersLoading, setRunningUsersLoading] = useState(false);

  const loadRunningUserOptions = useCallback(async () => {
    setRunningUsersLoading(true);
    try {
      const dashboardApi = new DataApi('dashboard');
      const res = await dashboardApi.get('/running-user-options?limit=500');
      const rows = res?.data?.success && Array.isArray(res.data.data) ? res.data.data : [];
      const opts = rows.map(runningUserApiRowToOption).filter(Boolean);
      const byId = new Map(opts.map((o) => [o.id, o]));
      const me = getLoggedInUserId();
      if (me && !byId.has(me)) {
        byId.set(me, { id: me, label: loggedInUserRunAsLabel() });
      }
      const merged = Array.from(byId.values()).sort((a, b) =>
        a.label.localeCompare(b.label, undefined, { sensitivity: 'base' })
      );
      setRunningUserOptions(merged);
    } catch (e) {
      console.error('Failed to load users for dashboard running user:', e);
      const me = getLoggedInUserId();
      if (me) {
        setRunningUserOptions([{ id: me, label: loggedInUserRunAsLabel() }]);
      } else {
        setRunningUserOptions([]);
      }
    } finally {
      setRunningUsersLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDashboards();
  }, []);

  useEffect(() => {
    filterDashboards();
  }, [dashboards, searchTerm]);

  // Keep select value valid when editing a dashboard whose running user is not in the fetched list
  useEffect(() => {
    if (!(showCreateModal || showEditModal)) return;
    const id = formData.running_user_id && String(formData.running_user_id).trim();
    if (!id) return;
    setRunningUserOptions((prev) => {
      if (prev.some((o) => o.id === id)) return prev;
      return [
        ...prev,
        { id, label: formatRunAsLabel(`User ${id.slice(0, 8)}…`, '—', '—') },
      ].sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
    });
  }, [showCreateModal, showEditModal, formData.running_user_id]);

  const loadDashboards = async () => {
    try {
      setLoading(true);
      setError(null);
      const dashboardApi = new DataApi('dashboard');
      const response = await dashboardApi.fetchAll();

      if (response.data.success) {
        const dashboardsData = response.data.data;

        setDashboards(dashboardsData);
      } else {
        setError('Failed to load dashboards');
      }
    } catch (err) {
      console.error('Error loading dashboards:', err);
      setError('Error loading dashboards. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const filterDashboards = () => {
    if (!searchTerm.trim()) {
      setFilteredDashboards(dashboards);
    } else {
      const filtered = dashboards.filter(dashboard =>
        dashboard.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (dashboard.label && dashboard.label.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (dashboard.description && dashboard.description.toLowerCase().includes(searchTerm.toLowerCase()))
      );
      setFilteredDashboards(filtered);
    }
  };

  const normalizeDashboardPayload = (raw) => {
    const payload = { ...raw };
    if (payload.view_type === 'LOGGED_IN_USER') {
      delete payload.running_user_id;
    } else {
      const uid =
        (payload.running_user_id && String(payload.running_user_id).trim()) ||
        getLoggedInUserId();
      payload.running_user_id = uid || null;
    }
    return payload;
  };

  const handleCreateDashboard = async (e) => {
    e.preventDefault();
    try {
      const dashboardApi = new DataApi('dashboard');
      const payload = normalizeDashboardPayload(formData);
      if (payload.view_type === 'STATIC_USER' && !payload.running_user_id) {
        setError('Please select a user for “Fixed user” dashboards.');
        return;
      }
      const response = await dashboardApi.create(payload);

      if (response.data.success) {
        setShowCreateModal(false);
        setFormData(emptyForm());
        loadDashboards(); // Reload the list
      } else {
        setError('Failed to create dashboard');
      }
    } catch (err) {
      console.error('Error creating dashboard:', err);
      setError('Error creating dashboard. Please try again.');
    }
  };

  const handleEditDashboard = async (e) => {
    e.preventDefault();
    try {
      const dashboardId = editingDashboard ? pickRecordId(editingDashboard) : '';
      if (!dashboardId) {
        setError('Missing dashboard id. Close the dialog and try again.');
        return;
      }

      const dashboardApi = new DataApi('dashboard');
      const payload = normalizeDashboardPayload(formData);
      if (payload.view_type === 'STATIC_USER' && !payload.running_user_id) {
        setError('Please select a user for “Fixed user” dashboards.');
        return;
      }
      console.log('Updating dashboard:', {
        id: dashboardId,
        formData: payload
      });
      const response = await dashboardApi.update(payload, dashboardId);

      if (response.data.success) {
        setShowEditModal(false);
        setEditingDashboard(null);
        setFormData(emptyForm());
        loadDashboards(); // Reload the list
      } else {
        setError('Failed to update dashboard');
      }
    } catch (err) {
      console.error('Error updating dashboard:', err);
      setError('Error updating dashboard. Please try again.');
    }
  };

  const handleDeleteDashboard = async (dashboardId) => {
    if (!window.confirm('Are you sure you want to delete this dashboard? This action cannot be undone.')) {
      return;
    }

    try {
      const dashboardApi = new DataApi('dashboard');
      const response = await dashboardApi.delete(`/${dashboardId}`);

      if (response.data.success) {
        loadDashboards(); // Reload the list
      } else {
        setError('Failed to delete dashboard');
      }
    } catch (err) {
      console.error('Error deleting dashboard:', err);
      setError('Error deleting dashboard. Please try again.');
    }
  };

  const openEditModal = (dashboard) => {
    setEditingDashboard(dashboard ? { ...dashboard } : null);
    const vt = dashboard.view_type || 'STATIC_USER';
    const ru =
      dashboard.running_user_id != null && String(dashboard.running_user_id).trim()
        ? String(dashboard.running_user_id).trim()
        : vt === 'STATIC_USER'
          ? getLoggedInUserId()
          : '';
    setFormData({
      name: dashboard.name || '',
      label: dashboard.label || '',
      description: dashboard.description || '',
      view_type: vt,
      running_user_id: ru,
    });
    setShowEditModal(true);
    loadRunningUserOptions();
  };

  const openCreateModal = () => {
    const me = getLoggedInUserId();
    setFormData({
      ...emptyForm(),
      running_user_id: me || '',
    });
    setShowCreateModal(true);
    loadRunningUserOptions();
  };

  const handleViewTypeChange = (viewType) => {
    setFormData((prev) => {
      const next = { ...prev, view_type: viewType };
      if (viewType === 'STATIC_USER') {
        const cur = prev.running_user_id && String(prev.running_user_id).trim();
        next.running_user_id = cur || getLoggedInUserId() || '';
      }
      return next;
    });
  };

  const closeModals = () => {
    setShowCreateModal(false);
    setShowEditModal(false);
    setEditingDashboard(null);
    setFormData(emptyForm());
  };

  const formatDashboardDate = (dateString) => {
    if (!dateString) return 'N/A';
    return formatDateTime(dateString);
  };

  /** At least current user while list loads; then full list from API */
  const runningUserSelectOptions =
    runningUserOptions.length > 0
      ? runningUserOptions
      : (() => {
          const me = getLoggedInUserId();
          return me ? [{ id: me, label: loggedInUserRunAsLabel() }] : [];
        })();

  // Convert label to name: lowercase, replace spaces/special chars with underscores
  const labelToName = (label) => {
    if (!label || typeof label !== 'string') return '';
    return label
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '') || '';
  };

  const handleLabelChange = (value, isEdit = false) => {
    setFormData(prev => ({
      ...prev,
      label: value,
      ...(isEdit ? {} : { name: labelToName(value) })
    }));
  };


  const handleOpenCanvas = (dashboardId) => {
    navigate(`/dashboard/${dashboardId}/canvas`);
  };

  const handleRunDashboard = (dashboardId) => {
    navigate(`/dashboard/${dashboardId}/run`);
  };


  if (loading) {
    return (
      <div className="dashboard-manager">
        <div className="dashboard-loading-container">
          <FaSpinner className="dashboard-spinner" />
          <p>Loading dashboards...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-manager">
      {/* Header */}
      <div className="dashboard-header">
        <div className="dashboard-header-content">
          <div className="dashboard-header-title">
            <FaChartBar className="dashboard-header-icon" />
            <h1>Dashboard Manager</h1>
            <span className="dashboard-count">({filteredDashboards.length} dashboards)</span>
          </div>
          <button
            className="dashboard-create-btn"
            onClick={openCreateModal}
          >
            <FaPlus />
            Create Dashboard
          </button>
        </div>
      </div>

      {/* Search and Filter */}
      <div className="dashboard-search-filter-section">
        <div className="dashboard-search-box">
          <FaSearch className="dashboard-search-icon" />
          <input
            type="text"
            placeholder="Search dashboards..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="dashboard-search-input"
          />
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="dashboard-error-message">
          <p>{error}</p>
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      {/* Dashboards List */}
      <div className="dashboard-dashboards-container">
        {filteredDashboards.length === 0 ? (
          <div className="dashboard-empty-state">
            <FaChartBar className="dashboard-empty-icon" />
            <h3>No dashboards found</h3>
            <p>
              {searchTerm
                ? 'No dashboards match your search criteria.'
                : 'Get started by creating your first dashboard.'
              }
            </p>
            {!searchTerm && (
              <button className="dashboard-create-btn" onClick={openCreateModal}>
                <FaPlus />
                Create Dashboard
              </button>
            )}
          </div>
        ) : (
          <div className="dashboard-table-container">
            <table className="dashboard-table">
              <thead>
                <tr>
                  <th>Label</th>
                  <th>Name</th>
                  <th>View as</th>
                  <th>Description</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredDashboards.map((dashboard) => (
                  <tr key={dashboard.id} className="dashboard-table-row">
                      <td className="dashboard-table-label">
                        {dashboard.label || dashboard.name}
                      </td>
                      <td className="dashboard-table-name">
                        {dashboard.name || '-'}
                      </td>
                      <td className="dashboard-table-run-as">
                        {dashboard.view_type === 'LOGGED_IN_USER'
                          ? 'Logged-in viewer'
                          : dashboard.running_user_id
                            ? formatRunAsLabel(
                                dashboard.running_user_full_name,
                                dashboard.running_user_username,
                                dashboard.running_user_role_name
                              )
                            : 'Fixed user (not set)'}
                      </td>
                      <td className="dashboard-table-description">
                        {dashboard.description || '-'}
                      </td>
                    <td className="dashboard-table-actions">
                      <button
                        className="dashboard-action-btn dashboard-run-btn"
                        onClick={() => handleRunDashboard(dashboard.id)}
                        title="Run Dashboard"
                      >
                        <FaPlay />
                      </button>
                      <button
                        className="dashboard-action-btn dashboard-canvas-btn"
                        onClick={() => handleOpenCanvas(dashboard.id)}
                        title="Open Canvas"
                      >
                        <FaChartBar />
                      </button>
                      <button
                        className="dashboard-action-btn dashboard-edit-btn"
                        onClick={() => openEditModal(dashboard)}
                        title="Edit Dashboard"
                      >
                        <FaEdit />
                      </button>
                      <button
                        className="dashboard-action-btn dashboard-delete-btn"
                        onClick={() => handleDeleteDashboard(dashboard.id)}
                        title="Delete Dashboard"
                      >
                        <FaTrash />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create Dashboard Modal */}
      {showCreateModal && (
        <div className="dashboard-modal-overlay">
          <div className="dashboard-modal">
            <div className="dashboard-modal-header">
              <h2>Create New Dashboard</h2>
              <button className="dashboard-close-btn" onClick={closeModals}>×</button>
            </div>
            <form onSubmit={handleCreateDashboard} className="dashboard-modal-form">
              <div className="dashboard-hint-info">
                <FaChartBar className="dashboard-hint-icon" />
                <p>
                  <strong>Important:</strong> At least one chart widget is required to create a functional dashboard.
                  After creating the dashboard, add chart widgets from the dashboard canvas.
                </p>
              </div>
              <div className="dashboard-form-group">
                <label htmlFor="label">Dashboard Label *</label>
                <input
                  type="text"
                  id="label"
                  value={formData.label}
                  onChange={(e) => handleLabelChange(e.target.value, false)}
                  required
                  placeholder="Enter dashboard label (e.g., Sales Overview)"
                />
              </div>
              <div className="dashboard-form-group">
                <label htmlFor="name">Dashboard Name *</label>
                <input
                  type="text"
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  placeholder="Auto-populated from label (e.g., sales_overview)"
                />
              </div>
              <div className="dashboard-form-group">
                <label htmlFor="description">Description</label>
                <textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Enter dashboard description"
                  rows="3"
                />
              </div>
              <div className="dashboard-form-group">
                <label htmlFor="view_type">Running user (view as)</label>
                <select
                  id="view_type"
                  value={formData.view_type}
                  onChange={(e) => handleViewTypeChange(e.target.value)}
                  className="dashboard-form-select"
                >
                  <option value="STATIC_USER">Fixed user (choose below; default is you)</option>
                  <option value="LOGGED_IN_USER">Viewer (whoever opens the dashboard)</option>
                </select>
              </div>
              {formData.view_type === 'STATIC_USER' && (
                <div className="dashboard-form-group">
                  <label htmlFor="running_user_id">Run dashboard as user *</label>
                  <select
                    id="running_user_id"
                    value={formData.running_user_id}
                    onChange={(e) =>
                      setFormData({ ...formData, running_user_id: e.target.value })
                    }
                    className="dashboard-form-select"
                    required
                    disabled={runningUserSelectOptions.length === 0}
                  >
                    {runningUserSelectOptions.map((o) => (
                      <option key={o.id} value={o.id}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                  {runningUsersLoading && (
                    <p className="small text-muted mt-1 mb-0">Loading user list…</p>
                  )}
                  <p className="dashboard-form-hint text-muted small mt-1 mb-0">
                    Each option shows <strong>Name | Username | Role</strong>. Defaults to your account.
                  </p>
                </div>
              )}
              <div className="dashboard-modal-actions">
                <button type="button" className="dashboard-cancel-btn" onClick={closeModals}>
                  Cancel
                </button>
                <button type="submit" className="dashboard-save-btn">
                  Create Dashboard
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Dashboard Modal */}
      {showEditModal && (
        <div className="dashboard-modal-overlay">
          <div className="dashboard-modal">
            <div className="dashboard-modal-header">
              <h2>Edit Dashboard</h2>
              <button className="dashboard-close-btn" onClick={closeModals}>×</button>
            </div>
            <form onSubmit={handleEditDashboard} className="dashboard-modal-form">
              <div className="dashboard-form-group">
                <label htmlFor="edit-label">Dashboard Label *</label>
                <input
                  type="text"
                  id="edit-label"
                  value={formData.label}
                  onChange={(e) => handleLabelChange(e.target.value, true)}
                  required
                  placeholder="Enter dashboard label"
                />
              </div>
              <div className="dashboard-form-group">
                <label htmlFor="edit-name">Dashboard Name *</label>
                <input
                  type="text"
                  id="edit-name"
                  value={formData.name}
                  required
                  disabled
                  readOnly
                  placeholder="Auto-generated (cannot be changed)"
                  className="dashboard-name-disabled"
                />
              </div>
              <div className="dashboard-form-group">
                <label htmlFor="edit-description">Description</label>
                <textarea
                  id="edit-description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Enter dashboard description"
                  rows="3"
                />
              </div>
              <div className="dashboard-form-group">
                <label htmlFor="edit-view_type">Running user (view as)</label>
                <select
                  id="edit-view_type"
                  value={formData.view_type}
                  onChange={(e) => handleViewTypeChange(e.target.value)}
                  className="dashboard-form-select"
                >
                  <option value="STATIC_USER">Fixed user (choose below; default is you)</option>
                  <option value="LOGGED_IN_USER">Viewer (whoever opens the dashboard)</option>
                </select>
              </div>
              {formData.view_type === 'STATIC_USER' && (
                <div className="dashboard-form-group">
                  <label htmlFor="edit-running_user_id">Run dashboard as user *</label>
                  <select
                    id="edit-running_user_id"
                    value={formData.running_user_id}
                    onChange={(e) =>
                      setFormData({ ...formData, running_user_id: e.target.value })
                    }
                    className="dashboard-form-select"
                    required
                    disabled={runningUserSelectOptions.length === 0}
                  >
                    {runningUserSelectOptions.map((o) => (
                      <option key={o.id} value={o.id}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                  {runningUsersLoading && (
                    <p className="small text-muted mt-1 mb-0">Loading user list…</p>
                  )}
                  <p className="dashboard-form-hint text-muted small mt-1 mb-0">
                    <strong>Name | Username | Role</strong>. Defaults to your account when switching to fixed user.
                  </p>
                </div>
              )}
              <div className="dashboard-modal-actions">
                <button type="button" className="dashboard-cancel-btn" onClick={closeModals}>
                  Cancel
                </button>
                <button type="submit" className="dashboard-save-btn">
                  Update Dashboard
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
};

export default DashboardManager;
